import {
  IMF_LABELS,
  formatCelsius,
  strongestImf,
  type ImfType,
  type Substance,
} from './intermolecularForces';

/** Chart scale in °C. Covers every reviewed normal boiling point (−246.1 °C to 100 °C). */
const SCALE_MIN_C = -260;
const SCALE_MAX_C = 120;
const SCALE_SPAN = SCALE_MAX_C - SCALE_MIN_C;
const TICKS_C = [-200, -100, 0, 100];

const IMF_BAR_CLASSES: Record<ImfType, string> = {
  dispersion: 'bg-brand-sky',
  'dipole-dipole': 'bg-brand-sunshine',
  'hydrogen-bonding': 'bg-brand-coral',
};

/** Short text cue so colour is never the only signal. */
const IMF_SHORT_LABELS: Record<ImfType, string> = {
  dispersion: 'Disp.',
  'dipole-dipole': 'Dipole',
  'hydrogen-bonding': 'H-bond',
};

const percent = (celsius: number) => ((celsius - SCALE_MIN_C) / SCALE_SPAN) * 100;

export function BoilingPointChart({ rows, label }: { rows: Substance[]; label: string }) {
  const zero = percent(0);
  return (
    <figure className="min-w-0" aria-label={label}>
      <ul className="grid gap-2">
        {rows.map((substance) => {
          const value = substance.boilingPointC ?? 0;
          const position = percent(value);
          const left = Math.min(position, zero);
          const width = Math.abs(position - zero);
          const strongest = strongestImf(substance);
          return (
            <li
              key={substance.id}
              className="grid grid-cols-[4.5rem_minmax(0,1fr)_4.5rem] items-center gap-2 text-xs font-bold text-ink-soft sm:grid-cols-[6.5rem_minmax(0,1fr)_5.5rem]"
              aria-label={`${substance.name}, ${substance.formula}: ${formatCelsius(value)}, strongest force ${IMF_LABELS[strongest].toLowerCase()}`}
            >
              <span className="truncate font-extrabold text-ink">{substance.formula}</span>
              <span className="relative h-5 rounded-full bg-surface" aria-hidden="true">
                <span className="absolute inset-y-0 w-px bg-ink" style={{ left: `${zero}%` }} />
                <span
                  className={`absolute inset-y-0.5 rounded-full ${IMF_BAR_CLASSES[strongest]}`}
                  style={{ left: `${left}%`, width: `${Math.max(width, 0.6)}%` }}
                />
              </span>
              <span className="text-right">
                {formatCelsius(value)}
                <span className="block text-[0.6rem] font-extrabold uppercase text-slate2">
                  {IMF_SHORT_LABELS[strongest]}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      <div className="mt-1 grid grid-cols-[4.5rem_minmax(0,1fr)_4.5rem] gap-2 sm:grid-cols-[6.5rem_minmax(0,1fr)_5.5rem]" aria-hidden="true">
        <span />
        <span className="relative h-4 text-[0.6rem] font-bold text-slate2">
          {TICKS_C.map((tick) => (
            <span key={tick} className="absolute -translate-x-1/2" style={{ left: `${percent(tick)}%` }}>
              {formatCelsius(tick).replace(' °C', '')}
            </span>
          ))}
        </span>
        <span />
      </div>
      <figcaption className="mt-3 flex flex-wrap gap-3 text-xs font-bold text-slate2">
        {(Object.keys(IMF_BAR_CLASSES) as ImfType[]).map((type) => (
          <span key={type} className="inline-flex items-center gap-1.5">
            <span className={`h-3 w-5 rounded-full ${IMF_BAR_CLASSES[type]}`} aria-hidden="true" />
            {IMF_SHORT_LABELS[type]} = strongest force is {IMF_LABELS[type].toLowerCase()}
          </span>
        ))}
        <span>Bars start at 0 °C · normal boiling point at 101.325 kPa</span>
      </figcaption>
    </figure>
  );
}
